import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  FiArrowLeft,
  FiArrowRight,
  FiMapPin,
  FiClock,
  FiSearch,
} from 'react-icons/fi';
import api from '../../utils/api';

const PAGE_BG = '#f8fafc';

/** Color for the plan-status chip; falls back to neutral grey. */
function statusChipColor(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return { bg: '#dcfce7', fg: '#166534' };
  if (s === 'trial') return { bg: '#dbeafe', fg: '#1e40af' };
  if (s === 'expired') return { bg: '#fee2e2', fg: '#991b1b' };
  return { bg: '#f1f5f9', fg: '#475569' };
}

function purchaseTypeLabel(purchaseType) {
  const norm = String(purchaseType || '').toLowerCase().replace(/\s+/g, '');
  if (norm === 'subscriptionuser') return 'Subscription user';
  if (norm === 'purchaseuser') return 'Purchase user';
  return purchaseType || '';
}

function usageBarColor(pct) {
  if (pct == null) return '#6366f1';
  if (pct >= 90) return '#dc2626';
  if (pct >= 70) return '#f59e0b';
  return '#16a34a';
}

function RecentSessionsTable({ sessions }) {
  const rows = Array.isArray(sessions) ? sessions : [];

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#64748b',
          mb: 0.75,
        }}
      >
        Recent (3 days)
      </Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
          No sessions in last 3 days
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: '0.35rem 0.75rem',
            alignItems: 'center',
            fontSize: '0.75rem',
            color: '#475569',
          }}
        >
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#94a3b8' }}>Room</Typography>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#94a3b8' }}>Date</Typography>
          <Typography
            sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#94a3b8', textAlign: 'right' }}
          >
            Min
          </Typography>
          {rows.map((row, idx) => (
            <React.Fragment key={`${row.room}-${row.date}-${idx}`}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#334155' }} noWrap>
                {row.room}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                {row.date}
              </Typography>
              <Typography
                sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0f172a', textAlign: 'right' }}
              >
                {row.minutes}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
}

function resolveUserDisplayName(user) {
  return user?.name || user?.organizationName || user?.email || '';
}

function resolveUserSubLine(user) {
  const name = String(user?.name || '').trim();
  const org = String(user?.organizationName || '').trim();
  if (name && org && name.toLowerCase() !== org.toLowerCase()) return org;
  return user?.email || '';
}

function UserTile({ user, onOpen }) {
  const displayName = resolveUserDisplayName(user);
  const subLine = resolveUserSubLine(user);
  const chip = statusChipColor(user.planStatus);
  const pct = user.usagePct;
  const hoursLabel =
    user.planHours != null
      ? `${user.hoursThisMonth} / ${user.planHours} hrs`
      : `${user.hoursThisMonth} hrs`;

  return (
    <Card
      onClick={onOpen}
      sx={{
        height: '100%',
        borderRadius: '16px',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 28px rgba(99, 102, 241, 0.18)',
          borderColor: 'rgba(99, 102, 241, 0.4)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              fontWeight: 700,
              background: 'linear-gradient(to right, #3B82F6 0%, #6366f1 100%)',
            }}
          >
            {displayName ? displayName.charAt(0).toUpperCase() : '?'}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 600, color: '#0f172a', fontSize: '1rem', lineHeight: 1.25 }}
              noWrap
            >
              {displayName}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.8125rem' }} noWrap>
              {subLine}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {user.planName ? (
            <Chip
              label={user.planName}
              size="small"
              sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 600 }}
            />
          ) : (
            <Chip
              label={purchaseTypeLabel(user.purchaseType)}
              size="small"
              sx={{ bgcolor: '#f1f5f9', color: '#475569', fontWeight: 600 }}
            />
          )}
          {user.planStatus ? (
            <Chip
              label={user.planStatus}
              size="small"
              sx={{ bgcolor: chip.bg, color: chip.fg, fontWeight: 600, textTransform: 'capitalize' }}
            />
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
          {user.city ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b' }}>
              <FiMapPin size={14} />
              <Typography sx={{ fontSize: '0.8125rem' }} noWrap>
                {user.city}
              </Typography>
            </Box>
          ) : null}
          {user.renewalDate ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b' }}>
              <FiClock size={14} />
              <Typography sx={{ fontSize: '0.8125rem' }}>Renews {user.renewalDate}</Typography>
            </Box>
          ) : null}
        </Box>

        <RecentSessionsTable sessions={user.recentSessions} />

        <Box sx={{ mt: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
              This month
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#0f172a', fontWeight: 700 }}>
              {hoursLabel}
              {pct != null ? ` (${pct}%)` : ''}
            </Typography>
          </Box>
          {user.planHours != null ? (
            <LinearProgress
              variant="determinate"
              value={Math.min(pct ?? 0, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: usageBarColor(pct),
                },
              }}
            />
          ) : null}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1.25,
              color: '#4f46e5',
              fontWeight: 600,
              fontSize: '0.8125rem',
            }}
          >
            <span>Open dashboard</span>
            <FiArrowRight size={15} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function UsersOverviewPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchUsers = async () => {
      try {
        const res = await api.get('/admin/users-overview');
        if (res.data?.success) {
          setUsers(res.data.users || []);
        } else {
          setError('Failed to load users.');
        }
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load users.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.organizationName, u.email, u.city, u.planName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [users, query]);

  const openUser = (email) =>
    navigate(`/admin/users/${encodeURIComponent(email)}/overview`);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: PAGE_BG }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <Button
          startIcon={<FiArrowLeft />}
          onClick={() => navigate('/admin-dashboard')}
          sx={{ textTransform: 'none', color: '#475569', mb: 1.5, fontWeight: 600 }}
        >
          Back to Admin Dashboard
        </Button>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
              User Dashboard
            </Typography>
            <Typography sx={{ color: '#64748b', mt: 0.5 }}>
              Browse every user and open their dashboard.
            </Typography>
          </Box>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users…"
            size="small"
            sx={{ minWidth: { xs: '100%', md: 280 }, bgcolor: '#fff', borderRadius: '8px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography sx={{ color: '#dc2626', py: 4 }}>{error}</Typography>
        ) : filtered.length === 0 ? (
          <Typography sx={{ color: '#64748b', py: 4 }}>No users found.</Typography>
        ) : (
          <Grid container spacing={3}>
            {filtered.map((user) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={user.email}>
                <UserTile user={user} onOpen={() => openUser(user.email)} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
